import classnames from 'classnames';
import { get } from 'lodash';
import orderBy from 'lodash/orderBy';
import { X } from 'lucide-react';
import React, { useRef } from 'react';
import { v4 } from 'uuid';

import { Label } from 'v1/components/shared';
import { cn } from 'v5/platform/dom/cn';
import { focusRings } from '../../../../../../v5/design-system/_common/focusRing';

interface MultiInputItem {
  id?: string;
  [key: string]: any;
}

interface MultiInputProps {
  id?: string; // Unused, just removing so it isn't spread
  field?: string; // Unused, just removing so it isn't spread
  label?: string;
  size?: 'S' | 'M' | 'L';
  disabled?: boolean;
  value?: MultiInputItem[];
  children: React.ReactElement;
  onBlur?: (val?: any) => void;
  onChange?: (value: MultiInputItem[]) => void;
  initialValues?: Record<string, any>; // Required if you want to pre-populate values to a newly added item
  // ...props - Inherits props from its child as they're spread on
  [key: string]: any;
}

function MultiInput({
  id,
  field,
  label,
  size = 'M',
  disabled,
  value = [],
  children,
  onBlur,
  onChange,
  initialValues,
  ...props
}: MultiInputProps) {
  const ref = useRef<HTMLDivElement>(null);

  const handleAdd = () => {
    onChange &&
      onChange([
        ...value,
        { id: `temp-${Date.now()}-${v4()}`, ...initialValues }
      ]);
    // Focus the latest input after clicking 'add'
    new Promise(resolve => setTimeout(resolve, 0)).then(() => {
      const focusable = ref.current?.querySelectorAll(
        '[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable && focusable.length) {
        const focusableElement = focusable[focusable.length - 1];
        if (focusableElement instanceof HTMLElement) {
          focusableElement.focus();
        }
      }
    });
  };

  const handleRemove = (itemId: string | undefined, index: number) => {
    if (onChange) {
      itemId
        ? onChange(value.filter(e => itemId !== e.id))
        : onChange(value.filter((e, i) => i !== index));
    }
    onBlur && onBlur();
  };

  const handleBlur = (index: number, val: any) => {
    /**
     * TODO react-form is deprecated and doesn't support immediate setValue + submitForm (setters and renders are now batched in React 18)
     *
     * So we have two sync writes, and in React 18, submitForm will read data from the form before the first write has been committed.
     */
    if (get(val, 'id')) {
      onChange && onChange(value.map(e => (e.id === val.id ? val : e)));
      setTimeout(() => {
        onBlur && onBlur();
      }, 0);
    } else {
      onChange && onChange(value.map((e, i) => (i === index ? val : e)));
      setTimeout(() => {
        onBlur && onBlur();
      }, 0);
    }
  };

  const sortedValue = orderBy(value, 'id'); // To stop multi-inputs switching order we'll order by ID

  return (
    <div ref={ref} className="tailwind-root">
      {label && <Label>{label}</Label>}
      {sortedValue.map((item, index) => (
        <div
          key={item.id || index}
          className={classnames(
            'w-full flex flex-row items-center not-last:mb-1.5',
            {
              'gap-0.5': size === 'S',
              'gap-1': size === 'M',
              'gap-2': size === 'L'
            },
            'group/multiInput'
          )}
        >
          <div className="w-full">
            {React.cloneElement(children, {
              size,
              disabled,
              value: item,
              onBlur: (val: any) => handleBlur(index, val),
              ...props
            })}
          </div>
          <div className="transition-opacity opacity-0 group-hocus/multiInput:opacity-100 w-auto">
            {!disabled && (
              <button
                className={classnames(
                  'ml-1 rounded-full size-5 flex items-center justify-center bg-bg-subtle text-fg-muted',
                  'cursor-pointer',
                  focusRings.focus.visible
                )}
                onClick={() => handleRemove(item.id, index)}
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
      ))}
      {!disabled && (
        <button
          className={cn(
            'underline w-full text-left font-semibold text-fg-default hocus:text-brand-500'
          )}
          onClick={handleAdd}
        >
          Add
        </button>
      )}
    </div>
  );
}

export default MultiInput;
